import type { ReactNode } from "react";

import {
	useGetCustomerAr,
	useGetCustomerCredit,
	useGetCustomerDetails,
} from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { HeaderField } from "./header-field.tsx";

const FALLBACK_DASH = "—";
const NONE_ASSIGNED = "None Assigned";

const getAvailableCredit = (quoteState?: QuoteStateType): number => {
	const credits = quoteState?.customerCredits;
	if (credits && typeof credits === "object" && "availableCredit" in credits) {
		return credits.availableCredit ?? 0;
	}
	return 0;
};

type QuoteDetailsHeaderProps = {
	quoteState?: QuoteStateType;
};

export const QuoteDetailsHeader = ({ quoteState }: QuoteDetailsHeaderProps) => {
	const { isInternal } = useDeploymentType();

	const customerId = quoteState?.customer?.id ?? 0;
	const brokerId = quoteState?.customer?.primaryBroker?.id ?? 0;

	const { isLoading: isCustomerDetailsLoading } =
		useGetCustomerDetails(customerId);
	const { arInfo, isLoading: isArLoading } = useGetCustomerAr(
		customerId,
		brokerId,
		isInternal,
	);
	const { isLoading: isCreditLoading } = useGetCustomerCredit(customerId);

	// The Quote ID is shown to all users (internal and external) directly to the
	// left of the Revise button. Internal users additionally see the full
	// customer summary fields.
	const quoteIdField = quoteState?.id ? (
		<HeaderField isLoading={false} title={"Quote ID:"}>
			{quoteState.id}
		</HeaderField>
	) : null;

	if (!isInternal) return quoteIdField;

	// Detect when customerDetails still holds data from a previous customer
	// (e.g. after revising a quote and changing the customer). Show loading
	// state instead of briefly flashing stale data.
	const isCustomerDataStale =
		customerId > 0 &&
		(quoteState?.customerDetails?.id ?? 0) > 0 &&
		quoteState?.customerDetails?.id !== customerId;

	const isLoading =
		!customerId ||
		isCustomerDetailsLoading ||
		isArLoading ||
		isCreditLoading ||
		isCustomerDataStale;

	const customerDetails = quoteState?.customerDetails;
	const customer = quoteState?.customer;

	const hasSignedLTLAddendum =
		customerDetails?.hasSignedLTLAddendum ??
		customer?.hasSignedLTLAddendum ??
		false;

	const fields: { title: string; value: ReactNode; skeletonWidth?: string }[] =
		[
			{
				title: "Customer:",
				value: customerDetails?.name || customer?.name || FALLBACK_DASH,
			},
			{
				title: "Status:",
				value:
					customerDetails?.status?.name ||
					customer?.status?.name ||
					FALLBACK_DASH,
			},
			{
				title: "AR:",
				value: arInfo?.name || quoteState?.customerAr?.name || NONE_ASSIGNED,
			},
			{
				title: "LAE:",
				value:
					customerDetails?.primaryBroker?.name ||
					customer?.primaryBroker?.name ||
					NONE_ASSIGNED,
			},
			{
				title: "Available Credit:",
				value: formatCurrency(getAvailableCredit(quoteState)),
				skeletonWidth: "100%",
			},
			{
				title: "Agreed to LTL Terms:",
				value: hasSignedLTLAddendum ? "Yes" : "No",
				skeletonWidth: "100%",
			},
		];

	return (
		<>
			{fields.map(({ title, value, skeletonWidth }) => (
				<HeaderField
					key={title}
					isLoading={isLoading}
					skeletonWidth={skeletonWidth}
					title={title}
				>
					{value}
				</HeaderField>
			))}
			{quoteIdField}
		</>
	);
};
